import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CarregadoresMoneyInput } from "@/components/CarregadoresMoneyInput";
import {
  WALLBOX_EXTRAS_COMPLEMENTARES_KEYS,
  WALLBOX_EXTRAS_META,
  WALLBOX_EXTRAS_PROJETO_KEYS,
  quantidadeExtraWallbox,
  type WallboxExtraItem,
  type WallboxExtraKey,
  type WallboxExtrasProposta,
} from "@/utils/wallboxExtrasPropostaUtils";

type WallboxExtrasFieldProps = {
  extras: WallboxExtrasProposta;
  onChange: (extras: WallboxExtrasProposta) => void;
  /** Exibe o kit movE Smart Charging. Usado na instalação de wallbox para condomínio. */
  incluirMove?: boolean;
};

function ExtraSwitchItem({
  extraKey,
  extras,
  onChange,
  helperText,
}: {
  extraKey: WallboxExtraKey;
  extras: WallboxExtrasProposta;
  onChange: (extras: WallboxExtrasProposta) => void;
  helperText?: string;
}) {
  const meta = WALLBOX_EXTRAS_META[extraKey];
  const item = extras[extraKey];
  const textoAjuda = helperText ?? meta.helperText;

  const atualizar = (patch: Partial<WallboxExtraItem>) => {
    onChange({
      ...extras,
      [extraKey]: { ...item, ...patch },
    });
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
        <div className="space-y-0.5">
          <Label htmlFor={`wb-extra-${extraKey}`} className="text-base">
            {meta.label}
          </Label>
          <p className="text-sm text-muted-foreground">{textoAjuda}</p>
        </div>
        <Switch
          id={`wb-extra-${extraKey}`}
          checked={item.incluir}
          onCheckedChange={(checked) => {
            if (checked && extraKey === "move") {
              onChange({
                ...extras,
                move: { ...item, incluir: true },
                clamper: { ...extras.clamper, incluir: false },
              });
              return;
            }
            if (checked && extraKey === "clamper") {
              onChange({
                ...extras,
                clamper: { ...item, incluir: true },
                move: { ...extras.move, incluir: false },
              });
              return;
            }
            atualizar({ incluir: checked });
          }}
        />
      </div>
      {item.incluir && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <CarregadoresMoneyInput
            id={`wb-extra-valor-${extraKey}`}
            label={meta.valorLabel}
            value={Number(item.valor) || 0}
            onValueChange={(n) => atualizar({ valor: n })}
            helperText="Valor unitário."
          />
          <div className="space-y-2">
            <Label htmlFor={`wb-extra-qtd-${extraKey}`}>Quantidade</Label>
            <Input
              id={`wb-extra-qtd-${extraKey}`}
              type="number"
              min={1}
              step={1}
              value={item.quantidade}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                atualizar({ quantidade: quantidadeExtraWallbox(n) });
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}

export function WallboxExtrasField({ extras, onChange, incluirMove = false }: WallboxExtrasFieldProps) {
  const complementares: WallboxExtraKey[] = incluirMove
    ? ["move", ...WALLBOX_EXTRAS_COMPLEMENTARES_KEYS]
    : [...WALLBOX_EXTRAS_COMPLEMENTARES_KEYS];

  return (
    <div className="space-y-4">
      {WALLBOX_EXTRAS_PROJETO_KEYS.map((extraKey) => (
        <ExtraSwitchItem
          key={extraKey}
          extraKey={extraKey}
          extras={extras}
          onChange={onChange}
        />
      ))}

      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium">Itens complementares</p>
          <p className="text-xs text-muted-foreground">
            Ative o item para informar valor unitário e quantidade.
          </p>
        </div>
        {complementares.map((extraKey) => (
          <ExtraSwitchItem
            key={extraKey}
            extraKey={extraKey}
            extras={extras}
            onChange={onChange}
            helperText={
              incluirMove && extraKey === "clamper"
                ? `${WALLBOX_EXTRAS_META.clamper.helperText} Não combine com o movE: as duas soluções não entram na mesma proposta.`
                : undefined
            }
          />
        ))}
      </div>
    </div>
  );
}
